/**
 * useDebugSnapshot — aggregates the full Debug Panel payload from existing
 * contexts + global stores. The output is what Copy / Export emits (after a
 * pass through the redaction pipeline).
 *
 * Sources (no new global stores introduced):
 *   - AuthContext        → identity, branch, override, status
 *   - ConfigContext      → catalog counts + system settings
 *   - apiCallLogStore    → API request/response log
 *   - errorBufferStore   → console + window + react boundary captures
 *   - performance.*      → navigation/markers
 *   - navigator / window → environment + viewport
 *   - localStorage       → feature flags + Test Mode override stash
 *
 * The Cart slice is read via a tiny safe-wrapper because `useCart()` throws
 * outside CartProvider — and the panel mounts at the shell level where Cart
 * is NOT in scope. Components that DO have cart state can extend the snapshot
 * via `window.__PCB_DEBUG_EXTRA__` (a ref-buffer the panel merges in). This
 * keeps the panel decoupled from the cart provider while still surfacing cart
 * state when present.
 */
import { useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import { APP_VERSION, APP_BUILD_TIME, APP_BUILD_CHANNEL } from '@/constants/version';
import { useTestMode } from './useTestMode';
import {
  computeLatencyStats,
  getApiCallLogSnapshot,
  type ApiCallLogEntry,
} from '../services/apiCallLogStore';
import {
  getErrorBufferSnapshot,
  type ErrorBufferEntry,
} from '../services/errorBufferStore';

/**
 * Anything not in a context can land here at runtime. Modules can write into
 * this slot to surface their state into the debug payload (e.g., RAO writes
 * the active CartDto + dialog state).
 *
 * Example consumer (RAO):
 *   useEffect(() => {
 *     window.__PCB_DEBUG_EXTRA__ = {
 *       ...(window.__PCB_DEBUG_EXTRA__ ?? {}),
 *       cart: { id: cart?.cartId, status: cart?.status, dirty },
 *       activeDialog: { name: 'PersonDialog', personId: ... },
 *     };
 *     return () => { delete (window.__PCB_DEBUG_EXTRA__ as any).cart; };
 *   }, [cart, dirty]);
 */
declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    __PCB_DEBUG_EXTRA__?: Record<string, unknown>;
  }
}

export interface DebugSnapshot {
  generatedAt: string;
  panelVersion: string;
  identity: Record<string, unknown>;
  cart: Record<string, unknown> | null;
  dialog: Record<string, unknown> | null;
  apiCallLog: ReadonlyArray<ApiCallLogEntry>;
  apiLatency: { count: number; p50: number; p99: number; mean: number };
  twain: Record<string, unknown> | null;
  config: Record<string, unknown>;
  routing: Record<string, unknown>;
  testMode: Record<string, unknown>;
  errors: ReadonlyArray<ErrorBufferEntry>;
  performance: Record<string, unknown>;
  environment: Record<string, unknown>;
  featureFlags: Record<string, unknown>;
}

const PANEL_VERSION = '0.1.0';

function countCatalogs(config: ReturnType<typeof useConfig>['config']): Record<string, number> {
  const c = config as unknown as Record<string, unknown>;
  const out: Record<string, number> = {};
  for (const key of Object.keys(c)) {
    const v = c[key];
    if (Array.isArray(v)) out[key] = v.length;
    else if (key === 'notificationTemplates' && v && typeof v === 'object') {
      const env = v as Record<string, unknown>;
      const tmpl = env['templates'];
      out['notificationTemplates.templates'] = Array.isArray(tmpl) ? tmpl.length : 0;
    }
  }
  return out;
}

function readPerformanceMarks(): Record<string, unknown> {
  if (typeof performance === 'undefined') return {};
  const out: Record<string, unknown> = {};
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (nav) {
      out.navigation = {
        type: nav.type,
        domContentLoaded: Math.round(nav.domContentLoadedEventEnd - nav.startTime),
        loadEvent: Math.round(nav.loadEventEnd - nav.startTime),
        responseEnd: Math.round(nav.responseEnd - nav.startTime),
        transferSize: nav.transferSize,
        encodedBodySize: nav.encodedBodySize,
        decodedBodySize: nav.decodedBodySize,
      };
    }
  } catch { /* ignore */ }
  try {
    const paints = performance.getEntriesByType('paint');
    if (paints.length > 0) {
      out.paint = paints.map((p) => ({ name: p.name, startTime: Math.round(p.startTime) }));
    }
  } catch { /* ignore */ }
  try {
    const marks = performance.getEntriesByType('mark');
    if (marks.length > 0) {
      out.marks = marks.slice(-20).map((m) => ({ name: m.name, startTime: Math.round(m.startTime) }));
    }
  } catch { /* ignore */ }
  if (typeof performance.now === 'function') out.now = Math.round(performance.now());
  if ('memory' in performance) {
    const mem = (performance as Performance & { memory?: { usedJSHeapSize?: number; totalJSHeapSize?: number; jsHeapSizeLimit?: number } }).memory;
    if (mem) out.memory = mem;
  }
  return out;
}

function readEnvironment(): Record<string, unknown> {
  const out: Record<string, unknown> = {
    appVersion: APP_VERSION,
    appBuildTime: APP_BUILD_TIME,
    appBuildChannel: APP_BUILD_CHANNEL,
    viteMode: import.meta.env.MODE,
    viteDev: import.meta.env.DEV,
    viteProd: import.meta.env.PROD,
    timestamp: new Date().toISOString(),
    timezoneOffsetMinutes: new Date().getTimezoneOffset(),
  };
  if (typeof navigator !== 'undefined') {
    out.userAgent = navigator.userAgent;
    out.platform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ?? navigator.platform;
    out.language = navigator.language;
    out.languages = navigator.languages;
    out.hasTouch = typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 0;
    out.onLine = navigator.onLine;
  }
  if (typeof window !== 'undefined') {
    out.viewport = {
      innerWidth: window.innerWidth,
      innerHeight: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
    };
    try {
      out.colorScheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      out.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      out.locale = Intl.DateTimeFormat().resolvedOptions().locale;
      out.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch { /* ignore */ }
  }
  return out;
}

function readFeatureFlags(): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  try {
    const env = import.meta.env as unknown as Record<string, unknown>;
    for (const key of Object.keys(env)) {
      if (key.startsWith('VITE_FEATURE_')) out[key] = env[key];
    }
  } catch { /* ignore */ }
  if (typeof window !== 'undefined') {
    try {
      // localStorage / sessionStorage runtime overrides (any key starting with
      // pcb.flag.* is surfaced).
      const ls: Record<string, unknown> = {};
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k && k.startsWith('pcb.flag.')) ls[k] = window.localStorage.getItem(k);
      }
      if (Object.keys(ls).length > 0) out.localStorageOverrides = ls;
    } catch { /* ignore */ }
  }
  return out;
}

function readRouting(): Record<string, unknown> {
  if (typeof window === 'undefined') return {};
  return {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    href: window.location.href,
    historyLength: window.history.length,
  };
}

export function useDebugSnapshot(): {
  build: () => DebugSnapshot;
} {
  const { user, isInTeams, token } = useAuth();
  const cfg = useConfig();
  const tm = useTestMode();

  const upn = user?.upn ?? '';
  const role = user?.primaryRole ?? '';
  const branchCode = user?.branchCode ?? '';
  const officerCode = user?.officerCode ?? '';

  const build = useCallback((): DebugSnapshot => {
    const extra = (typeof window !== 'undefined' && window.__PCB_DEBUG_EXTRA__) || {};
    const cart = (extra['cart'] as Record<string, unknown> | undefined) ?? null;
    const dialog = (extra['dialog'] as Record<string, unknown> | undefined) ?? null;
    const twain = (extra['twain'] as Record<string, unknown> | undefined) ?? null;
    return {
      generatedAt: new Date().toISOString(),
      panelVersion: PANEL_VERSION,
      identity: {
        upn,
        displayName: user?.displayName,
        primaryRole: role,
        roles: user?.roles,
        permissions: user?.permissions,
        tenantId: user?.tenantId,
        officerCode,
        officerTeam: user?.officerTeam,
        branchCode,
        primaryBranchCode: user?.primaryBranchCode,
        primaryBranchName: user?.primaryBranchName,
        activeBranchCode: user?.activeBranchCode,
        activeBranchName: user?.activeBranchName,
        overrideActive: user?.overrideActive,
        overrideSetAt: user?.overrideSetAt,
        overrideReason: user?.overrideReason,
        regionCode: user?.regionCode,
        effectiveLevel: user?.effectiveLevel,
        subordinatesEmails: user?.subordinatesEmails,
        // nmlsId + appDepartment dropped 2026-05-14.
        status: user?.status,
        isCompliance: user?.isCompliance,
        isKiosk: user?.isKiosk,
        isInTeams,
        hasToken: Boolean(token),
        tokenFingerprint: token ? `${token.slice(0, 8)}…(${token.length}ch)` : null,
        teamsContext: extra['teamsContext'] ?? null,
      },
      cart,
      dialog,
      apiCallLog: getApiCallLogSnapshot(),
      apiLatency: computeLatencyStats(),
      twain,
      config: {
        loadState: cfg.state,
        lastLoadedAt: cfg.lastLoadedAt?.toISOString(),
        error: cfg.error,
        catalogCounts: countCatalogs(cfg.config),
        systemSettings: cfg.config.systemSettings,
      },
      routing: readRouting(),
      testMode: {
        enabled: tm.enabled,
        source: tm.source,
        ...(extra['testMode'] as Record<string, unknown> ?? {}),
      },
      errors: getErrorBufferSnapshot(),
      performance: readPerformanceMarks(),
      environment: readEnvironment(),
      featureFlags: readFeatureFlags(),
    };
  }, [user, role, upn, branchCode, officerCode, isInTeams, token, cfg, tm]);

  return useMemo(() => ({ build }), [build]);
}
